import { arange } from 'utils/arange'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AbstractFn = (...args: any[]) => any

type PriorDataCache<GENERIC_FN extends AbstractFn> = {
  params: Parameters<GENERIC_FN>
  ret: ReturnType<GENERIC_FN>
}

/**
 * A function that memoizes another function based on all of its parameters.
 * - The signature of this function was set up to mirror lodash's `memoize` function,
 *   and it shares almost the same behavior except that it memoizes based on all parameters (not just the first one),
 *   and the cache is an array
 *
 * @param fn - the function to memoize
 */

export function memoizeOnAllParams<GENERIC_FN extends AbstractFn>(
  fn: GENERIC_FN
): (...args: Parameters<GENERIC_FN>) => ReturnType<GENERIC_FN> & {
  cache: Array<PriorDataCache<GENERIC_FN>>
} {
  // The cache object that will store the prior parameters and return value
  // NOTE: This re-initializes if this (outer) function is called again
  // eslint-disable-next-line prefer-const -- Cache is not readonly, so I want this to be a "let" to emphasize that even through the reference is stable, the contents are not
  let cache: Array<PriorDataCache<GENERIC_FN>> = []

  function memoized(...args: Parameters<GENERIC_FN>): ReturnType<GENERIC_FN> {
    const cachedValue = cache.find(({ params: curCacheParams }) => {
      if (curCacheParams.length !== args.length) return false
      return arange(0, curCacheParams.length).every(
        (i) => curCacheParams[i] === args[i]
      )
    })
    if (!cachedValue) {
      const ret = fn(...args)
      cache.push({ params: args, ret })
      return ret
    }

    return cachedValue.ret
  }

  memoized.cache = cache

  return memoized
}
